import { useEffect, useRef, useState } from "react";
import type { Video } from "@/data/videos";

const toSeconds = (duration: string): number => {
  const parts = duration.split(":").map(Number);
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  return 0;
};

const formatTime = (sec: number): string => {
  const s = Math.max(0, Math.floor(sec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  const mm = `${m}`.padStart(2, "0");
  const ss = `${r}`.padStart(2, "0");
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
};

export default function PlayerDemo({ video }: { video: Video }) {
  const totalSeconds = toSeconds(video.duration) || 90 * 60;
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    if (!playing) return;
    timerRef.current = window.setInterval(() => {
      setProgress((p) => {
        if (p >= 100) {
          setPlaying(false);
          return 100;
        }
        return p + 0.5;
      });
    }, 100);
    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
    };
  }, [playing]);

  const currentTime = (progress / 100) * totalSeconds;

  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = (e.clientX - rect.left) / rect.width;
    setProgress(Math.min(100, Math.max(0, ratio * 100)));
  };

  return (
    <div>
      <div className="relative aspect-video w-full overflow-hidden rounded-lg bg-foreground-950">
        <img
          src={video.cover}
          alt={video.title}
          title={`${video.title} 免费在线观看`}
          className={`h-full w-full object-cover transition-opacity duration-500 ${
            playing ? "opacity-30" : "opacity-90"
          }`}
        />
        <div className="absolute inset-0 bg-foreground-950/30"></div>

        {!playing ? (
          <button
            type="button"
            onClick={() => setPlaying(true)}
            aria-label="播放演示"
            className="absolute left-1/2 top-1/2 flex h-16 w-16 -translate-x-1/2 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-primary-500 text-background-50 transition-transform duration-300 hover:scale-110 md:h-20 md:w-20"
          >
            <i className="ri-play-fill text-3xl md:text-4xl"></i>
          </button>
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-50/15 text-background-50 backdrop-blur">
              <i className="ri-equalizer-line animate-pulse text-2xl"></i>
            </span>
            <p className="mt-3 text-sm font-medium text-background-100">
              正在演示播放 · {video.title}
            </p>
          </div>
        )}

        <span className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-md bg-accent-500/90 px-2 py-1 text-xs font-label font-semibold text-background-50">
          <i className="ri-information-line"></i>
          演示播放
        </span>

        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-foreground-950/90 to-transparent px-3 pb-3 pt-8 md:px-4 md:pb-4">
          <div
            className="group h-1.5 w-full cursor-pointer rounded-full bg-background-50/30"
            onClick={handleSeek}
            role="presentation"
          >
            <div
              className="relative h-full rounded-full bg-primary-400"
              style={{ width: `${progress}%` }}
            >
              <span className="absolute right-0 top-1/2 h-3 w-3 -translate-y-1/2 translate-x-1/2 rounded-full bg-background-50"></span>
            </div>
          </div>
          <div className="mt-2 flex items-center justify-between text-xs font-label text-background-100">
            <div className="flex items-center gap-4">
              <button
                type="button"
                onClick={() => setPlaying((v) => !v)}
                aria-label={playing ? "暂停" : "播放"}
                className="flex cursor-pointer items-center text-lg text-background-50"
              >
                <i className={playing ? "ri-pause-fill" : "ri-play-fill"}></i>
              </button>
              <button
                type="button"
                onClick={() => setProgress(0)}
                aria-label="重新播放"
                className="flex cursor-pointer items-center text-lg text-background-50"
              >
                <i className="ri-restart-line"></i>
              </button>
              <span>
                {formatTime(currentTime)} / {formatTime(totalSeconds)}
              </span>
            </div>
            <div className="flex items-center gap-3 text-lg text-background-50">
              <i className="ri-volume-up-line"></i>
              <i className="ri-fullscreen-line"></i>
            </div>
          </div>
        </div>
      </div>

      <p className="mt-3 flex items-start gap-2 rounded-md bg-background-100 px-3 py-2 text-xs leading-relaxed text-foreground-500">
        <i className="ri-alert-line mt-0.5 text-accent-500"></i>
        当前为前端演示播放，真实视频播放需接入服务端视频源后使用。演示内容仅用于展示播放器交互效果。
      </p>
    </div>
  );
}